import React, { useCallback } from 'react';

import { CommonActions, useNavigation } from '@react-navigation/native';

import { EntryInfoFragment } from '@/api/entries/query.generated';
import { ReuseIcon } from '@/assets/icons/reuse';
import { ShareEntryIcon } from '@/assets/icons/shareEntry';
import { ViewEntryIcon } from '@/assets/icons/viewEntry';
import { EntryActionButton } from '@/components/checkout/EntryActionButton';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useRateApp } from '@/feature/rate-app/hooks/use-rate-app';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { SubmitEntryNavigationProp } from '@/navigation/pickem/types';

import { PlaceEntryStatus } from '../hooks/use-place-entry';

type Props = {
    entry: EntryInfoFragment | undefined;
    isMysteryMode: boolean;
    isRaceToPlaceEntry: boolean;
    operationStatus: PlaceEntryStatus;
    spinAgainAmount: number;
    onSpinAgainPress: () => void;
    onReusePress: () => void;
};

export const EntryConfirmationButtons = ({
    entry,
    isMysteryMode,
    isRaceToPlaceEntry,
    operationStatus,
    spinAgainAmount,
    onSpinAgainPress,
    onReusePress,
}: Props) => {
    const navigation = useNavigation<SubmitEntryNavigationProp>();
    const { maybeShowAppRateAfterEntryPlaced } = useRateApp();

    const reuseShareLineupEnabled = useJurisdictionStore(
        state => state.jurisdictionSettings?.productConfig?.settings.enable_share_and_reuse_lineups
    );

    const canSpinAgain = isMysteryMode && operationStatus === 'success' && spinAgainAmount > 0;
    const canShare = reuseShareLineupEnabled;
    const canReuse = reuseShareLineupEnabled && !canSpinAgain && !isRaceToPlaceEntry;
    const handleShare = useCallback(() => {
        BetrAnalytics.trackEvent(AnalyticsEvent.SHARE_ENTRY, { screen: 'entry_submitted' });
        if (entry?.id) {
            navigation.navigate('EntryShare', { id: entry.id });
        }
    }, [entry?.id, navigation]);

    const handleReuse = useCallback(() => {
        BetrAnalytics.trackEvent(AnalyticsEvent.REUSE_LINEUP);
        onReusePress();
        navigation.goBack();
    }, [navigation, onReusePress]);

    const handleView = useCallback(() => {
        // after closing the EntryScreen, we should be redirected on Lobby
        navigation.dispatch(
            CommonActions.reset({
                index: 1,
                routes: [
                    { name: 'PickemHome' },
                    {
                        name: 'EntryScreen',
                        params: { id: entry?.id ?? '' },
                    },
                ],
            })
        );
        maybeShowAppRateAfterEntryPlaced({ delay: 2000 });
    }, [entry?.id, maybeShowAppRateAfterEntryPlaced, navigation]);

    return (
        <>
            {canSpinAgain ? (
                <EntryActionButton
                    icon={<ReuseIcon />}
                    label={`$${spinAgainAmount} re-spin`}
                    testID="entryConfirmationRespinButton"
                    onPress={onSpinAgainPress}
                />
            ) : null}
            {canShare ? (
                <EntryActionButton
                    icon={<ShareEntryIcon />}
                    label="Share"
                    testID="entryConfirmationShareEntryButton"
                    onPress={handleShare}
                />
            ) : null}
            {canReuse ? (
                <EntryActionButton
                    icon={<ReuseIcon />}
                    label="Reuse"
                    testID="entryConfirmationReuseLineupButton"
                    onPress={handleReuse}
                />
            ) : null}
            <EntryActionButton
                icon={<ViewEntryIcon />}
                label="View"
                testID="entryConfirmationViewEntryButton"
                onPress={handleView}
            />
        </>
    );
};
